import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { NotifyService } from '@shared/services/notify/notify.service';
import { handleHttpError } from '@shared/utils/handle-http-error.util';

interface IdentityValidationStatusResponse {
	result: boolean;
	data: {
		isNew: boolean;
		isVerified: boolean;
	};
	timestamp: string;
	status: number;
}

export interface IdentityValidationAccessState {
	showModal: boolean;
	closable: boolean;
	blocked: boolean;
}

const BLOCKED_STATE: IdentityValidationAccessState = {
	showModal: true,
	closable: false,
	blocked: true
};

const STATUS_CACHE_TTL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class IdentityValidationAccessService {
	private readonly apiUrl = '/api/v1';

	private statusCache: {
		userId: number;
		value: IdentityValidationStatusResponse['data'];
		expiresAt: number;
	} | null = null;

	private statusRequest$: Observable<IdentityValidationStatusResponse['data']> | null = null;
	private statusRequestUserId: number | null = null;
	private statusRequestSequence = 0;
	private onboardingReady = true;

	constructor(
		private readonly http: HttpClient,
		private readonly notifyService: NotifyService
	) {}

	getStatus(options: { forceRefresh?: boolean } = {}): Observable<IdentityValidationAccessState> {
		if (sessionStorage.getItem('isMasterSession') === '1') {
			return of({ showModal: false, closable: true, blocked: false });
		}

		const userId = this.resolveUserId();

		if (!userId) {
			this.notifyService.error(
				'No se encontró la información del usuario para consultar la validación de identidad.',
				'No se pudo validar la identidad'
			);
			return of(BLOCKED_STATE);
		}

		return this.getUserStatus(userId, options.forceRefresh).pipe(
			map(data => this.resolveAccessState(data)),
			catchError((error: HttpErrorResponse) => {
				this.showHttpError(error);
				return of(BLOCKED_STATE);
			})
		);
	}

	isNewUser(userId: number): Observable<boolean> {
		return this.getUserStatus(userId).pipe(map(data => {
			if (typeof data?.isNew !== 'boolean') {
				throw new TypeError('Account no devolvió un estado isNew válido.');
			}
			return data.isNew;
		}));
	}

	private getUserStatus(userId: number, forceRefresh = false): Observable<IdentityValidationStatusResponse['data']> {
		if (!forceRefresh) {
			const cachedStatus = this.getCachedStatus(userId);
			if (cachedStatus) {
				return of(cachedStatus);
			}

			if (this.statusRequest$ && this.statusRequestUserId === userId) {
				return this.statusRequest$;
			}
		}

		const requestSequence = ++this.statusRequestSequence;
		this.statusRequestUserId = userId;

		this.statusRequest$ = this.http
			.get<IdentityValidationStatusResponse>(`${this.apiUrl}/account/user/${userId}/is-new`)
			.pipe(
				map(response => response.data),
				tap(data => {
					if (this.statusRequestSequence === requestSequence) {
						this.setCachedStatus(userId, data);
					}
				}),
				finalize(() => {
					if (this.statusRequestSequence === requestSequence) {
						this.statusRequest$ = null;
						this.statusRequestUserId = null;
					}
				}),
				shareReplay({ bufferSize: 1, refCount: false })
			);

		return this.statusRequest$;
	}

	refreshStatus(): Observable<IdentityValidationAccessState> {
		return this.getStatus({ forceRefresh: true });
	}

	beginOnboardingGate(): void {
		this.onboardingReady = false;
	}

	completeOnboardingGate(): void {
		this.onboardingReady = true;
	}

	isOnboardingReady(): boolean {
		return this.onboardingReady;
	}

	isUserVerified(userId: number): Observable<boolean> {
		if (sessionStorage.getItem('isMasterSession') === '1') {
			return of(true);
		}

		return this.http
			.get<IdentityValidationStatusResponse>(`${this.apiUrl}/account/user/${userId}/is-new`)
			.pipe(
				map(response => response.data?.isVerified === true),
				catchError((error: HttpErrorResponse) => {
					this.showHttpError(error);
					return of(false);
				})
			);
	}

	clearStatusCache(): void {
		this.statusCache = null;
		this.statusRequest$ = null;
		this.statusRequestUserId = null;
		this.statusRequestSequence += 1;
	}

	private getCachedStatus(userId: number): IdentityValidationStatusResponse['data'] | null {
		if (this.statusCache?.userId !== userId) {
			return null;
		}

		if (this.statusCache.expiresAt <= Date.now()) {
			this.statusCache = null;
			return null;
		}

		return this.statusCache.value;
	}

	private setCachedStatus(userId: number, value: IdentityValidationStatusResponse['data']): void {
		this.statusCache = {
			userId,
			value,
			expiresAt: Date.now() + STATUS_CACHE_TTL_MS
		};
	}

	private resolveAccessState(data: IdentityValidationStatusResponse['data']): IdentityValidationAccessState {
		if (data?.isVerified === true) {
			return {
				showModal: false,
				closable: true,
				blocked: false
			};
		}

		if (data?.isNew === false) {
			return {
				showModal: true,
				closable: true,
				blocked: false
			};
		}

		return BLOCKED_STATE;
	}

	private resolveUserId(): number | null {
		try {
			const userInfo = JSON.parse(localStorage.getItem('user_info') || 'null');
			const userId = Number(userInfo?.id);

			return Number.isInteger(userId) && userId > 0 ? userId : null;
		} catch {
			return null;
		}
	}

	private showHttpError(error: HttpErrorResponse): void {
		const { notifyTitle, notifyMessage, notifyType } = handleHttpError(error);

		this.notifyService.show({
			title: notifyTitle,
			message: notifyMessage,
			type: notifyType
		});
	}
}
